import React from 'react';
import { useNavigate } from 'react-router-dom';
import css from '@/css/module/billing.css';
import flex from '@/css/module/flex.css';
import {
  SUBSCRIPTION_TAG,
  subscriptionApi,
  TStripePlan,
  TSubscription,
} from '@/features/subscription/apiSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MONTH_DAY_YEAR_OPTION } from '@/components/ChartFormattingOptions';
import {
  errorMessage,
  formatPrice,
  getIsCreditPricing,
  successMessage,
} from '@/features/subscription/components/utils';
import { NearestTiersWidget } from '@/features/credits/components/NearestTiersWidget';
import { useSelector } from 'react-redux';
import { closeReactModal, openReactModal } from '@/actions';
import ChangePlanModal from '@/components/billing/ChangePlanModal';
import { Button } from '@trevor/ui-components';
import { useAppDispatch } from '@/store/hooks';

const planName = (planId: string, plans: TStripePlan[]) => {
  return getPlan(planId, plans)?.title;
};

const getPlan = (planId: string, plans: TStripePlan[]) =>
  plans.find((p) => p.id === planId);

export const StripePlan: React.FC<{
  subscription: TSubscription;
  selectedPlanId: string;
  onPlanChange: F<string>;
  selectedPlan: TStripePlan;
}> = ({ subscription, selectedPlanId, onPlanChange, selectedPlan }) => {
  const isPayingCustomer = !!subscription.subscriptionStatus;
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const isCreditPricing = useSelector(getIsCreditPricing);

  const onOpenChangePlanModal = (
    displayPlans: TStripePlan[],
    existingPlan: TStripePlan,
    done: F2<string, boolean>,
    showCoupon: boolean,
  ) => {
    const closeModal =
      (isUpdated: boolean) => (updateDate: string, wasSuccessful: boolean) => {
        dispatch(closeReactModal());
        if (isUpdated) {
          done(updateDate, wasSuccessful);
        }
      };
    dispatch(
      openReactModal(
        <ChangePlanModal
          closeModal={closeModal(false)}
          onUpdated={closeModal(true)}
          plans={displayPlans}
          navigate={navigate}
          existingPlan={existingPlan}
          onSaveForNewCustomers={onPlanChange}
          isPayingCustomer={isPayingCustomer}
          formatPrice={(planID: string, isShowCoupon: boolean) =>
            formatPrice(
              getPlan(planID, subscription.stripePlans)?.startingPrice || 0,
              isShowCoupon,
              getPlan(planID, subscription.stripePlans)?.currency || '',
            )
          }
          showCoupon={showCoupon}
        />,
      ),
    );
  };

  const onPlanChanged = (updateDate: string, wasSuccessful: boolean) => {
    if (!wasSuccessful) {
      errorMessage('Problem upgrading plan.');
    } else if (updateDate) {
      successMessage(
        'Your downgrade will take effect from the end of your current billing period.',
        'Downgrade successful!',
      );
    } else {
      successMessage('Upgrade successful!');
    }
    dispatch(subscriptionApi.util.invalidateTags([SUBSCRIPTION_TAG]));
  };

  return (
    <div className={`${css.section} ${flex.row} ${flex.alignItemsStart}`}>
      <div className={css.billingIcon}>
        <FontAwesomeIcon icon="gamepad" />
      </div>
      <div>
        <div className={flex.column}>
          <span className={`${flex.row} ${flex.wrap}`}>
            {isPayingCustomer ? (
              <span>
                <b>Your plan:</b>
              </span>
            ) : (
              <span>
                <b>Select your plan:</b>
              </span>
            )}
            &nbsp;
            {planName(selectedPlanId, subscription.stripePlans)}
            {isPayingCustomer ? null : subscription.coupon ? (
              <em>
                &nbsp; (
                <span className="strike-through">
                  {formatPrice(
                    selectedPlan.price,
                    false,
                    selectedPlan.currency,
                  )}
                </span>
                &nbsp;
                <span>
                  {formatPrice(selectedPlan.price, true, selectedPlan.currency)}{' '}
                  / month
                </span>
                )
              </em>
            ) : (
              <em>
                &nbsp;(
                {formatPrice(selectedPlan.price, false, selectedPlan.currency)})
              </em>
            )}
            &nbsp;
            {subscription.nextPlanAt && (
              <span className={flex.wrap}>
                (downgrading to "
                {planName(subscription.nextPlan, subscription.stripePlans)}" on{' '}
                {MONTH_DAY_YEAR_OPTION.formatting(subscription.nextPlanAt)})
              </span>
            )}
            &nbsp;
            <span
              onClick={() =>
                onOpenChangePlanModal(
                  subscription.stripePlans,
                  selectedPlan,
                  onPlanChanged,
                  subscription.coupon && !isPayingCustomer,
                )
              }
              className="link"
            >
              Change
            </span>
            &nbsp;&nbsp;
            <Button
              variant="secondary"
              size="small"
              style={{ marginTop: '-5px' }}
              onClick={() => window.open('https://trevor.io/pricing', '_blank')}
            >
              View all features
            </Button>
          </span>
          {!isPayingCustomer && isCreditPricing && (
            <NearestTiersWidget
              selectedPlan={selectedPlanId}
              className={css.creditUsage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
